import union from 'lodash/union';
import { Moment } from 'moment';

import { WorkShiftSlot } from 'api/graphql';
import { DateFormat } from 'consts/date';
import { format } from 'utils';

const toFullDateTime = (date: string, time?: string) => {
  if (!date || !time) {
    return undefined;
  }

  const parsed = format.date({
    date: `${date} ${time}`,
    format: `${DateFormat.DateStamp} ${DateFormat.HourMinutes}`,
    strict: true,
  });

  if (parsed.isValid()) {
    return parsed.format(DateFormat.Full);
  }

  const parsedWithSeconds = format.date({
    date: `${date} ${time}`,
    format: `${DateFormat.DateStamp} ${DateFormat.HourMinuteSeconds}`,
    strict: true,
  });

  return parsedWithSeconds.isValid()
    ? parsedWithSeconds.format(DateFormat.Full)
    : undefined;
};

/**
 * Data format: `${date}__${name}__${uuidv4()}?__${workShiftId}`: `${value}`
 */
const collectSlots = (data: {
  [key: string]: any;
}): {
  endTime: string;
  startTime: string;
  resourceQuantity: number;
  id?: string | number;
}[] => {
  let uniqueIdentifiers: string[] = [];
  let workShiftSlots: {
    [key: string]: any;
  } = {};
  const dataKeys = Object.keys(data);
  const allowedNames = ['endTime', 'startTime', 'resourceQuantity'];

  dataKeys.forEach((key) => {
    const [, name, id] = key.split('__');

    if (allowedNames.includes(name)) {
      uniqueIdentifiers = union([...uniqueIdentifiers, id]);
    }
  });

  uniqueIdentifiers.forEach((uniqueIdentifier) => {
    dataKeys.forEach((key) => {
      const [date, name, uuidv4, workShiftId] = key.split('__');
      const value = data[key];

      if (uniqueIdentifier === uuidv4 && allowedNames.includes(name)) {
        workShiftSlots = {
          ...workShiftSlots,
          [uniqueIdentifier]: {
            ...workShiftSlots[uniqueIdentifier],
            ...(workShiftId ? { id: workShiftId } : {}),
            [name]:
              name === 'resourceQuantity'
                ? Number(value)
                : toFullDateTime(date, value),
          },
        };
      }
    });
  });

  return Object.keys(workShiftSlots).map((key) => {
    const workShiftSlot = workShiftSlots[key];
    const startTime = format.date({ date: workShiftSlot.startTime });
    const endTime = format.date({ date: workShiftSlot.endTime });

    if (
      startTime.isValid() &&
      endTime.isValid() &&
      endTime.isSameOrBefore(startTime)
    ) {
      workShiftSlot.endTime = endTime.add(1, 'days').format(DateFormat.Full);
    }

    return workShiftSlot;
  });
};

const hasInvalidSlotTimes = (workShiftSlots: ReturnType<typeof collectSlots>) =>
  workShiftSlots.some(({ startTime, endTime }) => {
    return (
      !startTime ||
      !endTime ||
      !format.date({ date: startTime }).isValid() ||
      !format.date({ date: endTime }).isValid()
    );
  });

const resolveSlotTimes = (
  workShiftSlots: ReturnType<typeof collectSlots>,
  existingSlots?: WorkShiftSlot[]
) => {
  if (!existingSlots?.length) {
    return workShiftSlots;
  }

  return workShiftSlots.map((slot) => {
    if (!slot.id) {
      return slot;
    }

    const existing = existingSlots.find(({ id }) => id === slot.id);

    if (!existing) {
      return slot;
    }

    const startTime = format.date({ date: slot.startTime });
    const endTime = format.date({ date: slot.endTime });
    const existingStartTime = format.date({ date: existing.startTime });
    const existingEndTime = format.date({ date: existing.endTime });

    return {
      ...slot,
      startTime: startTime.isValid()
        ? slot.startTime
        : existingStartTime.format(DateFormat.Full),
      endTime: endTime.isValid()
        ? slot.endTime
        : existingEndTime.format(DateFormat.Full),
    };
  });
};

/**
 * Format slots so the ones with the same "start time day" will be in the same
 * array
 */
export type FormatSlots = {
  date: Moment;
  workShiftSlots?: {
    endTime: string;
    startTime: string;
    resourceQuantity: number;
    id: string | number;
    fullyBooked: boolean;
  }[];
}[];

const formatSlots = (slots: WorkShiftSlot[]) => {
  let dates: FormatSlots = [];

  slots.forEach(({ startTime, endTime, resourceQuantity, id, fullyBooked }) => {
    const parsedStartTime = format.date({ date: startTime });
    const parsedEndTime = format.date({ date: endTime });

    if (!parsedStartTime.isValid() || !parsedEndTime.isValid()) {
      return;
    }

    const getDate = dates.find(({ date }) =>
      date.isSame(parsedStartTime, 'day')
    );
    const workShiftSlot = {
      startTime: parsedStartTime.format(DateFormat.HourMinutes),
      endTime: parsedEndTime.format(DateFormat.HourMinutes),
      resourceQuantity,
      id,
      fullyBooked,
    };

    if (getDate) {
      dates = [
        ...dates.filter(({ date }) => !date.isSame(parsedStartTime, 'day')),
        {
          ...getDate,
          workShiftSlots: [...(getDate?.workShiftSlots || []), workShiftSlot],
        },
      ];
      return;
    }

    dates = [
      ...dates,
      {
        date: parsedStartTime,
        workShiftSlots: [workShiftSlot],
      },
    ];
  });

  return dates;
};

export const workShift = {
  collectSlots,
  formatSlots,
  hasInvalidSlotTimes,
  resolveSlotTimes,
};
